import { LitElement, html, TemplateResult, PropertyValues } from 'lit';
import { property, customElement, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { IncrementInputEvent, InputConfig } from '../types';
import { ifDefined } from 'lit/directives/if-defined.js';
import { isEmpty } from '@nirvana/shared/basic-types/object';
import { debounce, toNumber } from 'lodash';

@customElement('gz-increment-input')
export class GzIncrementInputWebComponent extends LitElement {
  @property({ type: Object, attribute: true, reflect: true })
  inputConfig?: InputConfig;

  @property({ type: Object, attribute: true, reflect: true })
  incrementBtnValues: number[] = [10, 15, 20];

  @property({ type: Number, attribute: true, reflect: true })
  value: number = 0;

  @property({ type: Number, attribute: true, reflect: true })
  increment = 1;

  @query('#input-field')
  private inputElement!: HTMLInputElement;

  @state()
  private inputInteraction: boolean;

  @state()
  oldValue: number = 0;

  static get styles() {
    return styles;
  }

  render(): TemplateResult {
    return html`
      <div class="d-flex flex-row">
        <button
          class="number-control"
          automationId='gz-increment-input-button-decrement'
          @click="${this.decrementHandler}"
        >
          -
        </button>
        <div class="d-flex flex-column input-container">
          <div class="input input--number">
            <input
            id="input-field"
            class="input--field"
              type="number"
              value="${this.value}"
              @input="${this.handleTypingInNativeInput}"
            ></input>
          </div>
          <div class="d-flex flex-row increment-box">
            ${this.incrementBtnValues.map((item: number) => this.renderIncrementButtons(item))}
          </div>
        </div>
        <button
          class="number-control"
          automationId='gz-increment-input-button-increment'
          @click="${this.incrementHandler}"
        >
          +
        </button>
      </div>
    `;
  }

  private renderIncrementButtons(increment: number): TemplateResult {
    return html`<button
      automationId="gz-increment-input-button-increment-by-${increment}"
      @click="${() => this.handleIncrementButtons(increment)}"
    >
      +${increment}
    </button>`;
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    this.oldValue = this.value;
  }

  protected updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);
    if (_changedProperties.has('value')) {
      if (!isEmpty(this.value)) {
        this.dispatchMinMaxEvents(this.value);
        this.value = this.getValueRestrictedToMinMax(this.value as number);
        this.inputElement.value = this.value.toString();
        if (this.value !== this.oldValue) {
          this.oldValue = this.value;
          this.dispatchEvents(IncrementInputEvent.INCREMENT_CHANGE);
        }
      }
    }
  }

  protected handleTypingInNativeInput() {
    //increased the debounce time to 600ms so the user have more type to type the number
    const debouncedInputFormattingEvent = debounce(() => {
      this.value = toNumber(this.inputElement.value);
      this.inputElement.value = !isEmpty(this.inputElement.value)
        ? this.getValueRestrictedToMinMax(toNumber(this.inputElement.value)).toString()
        : '0';
    }, 1000);
    this.inputInteraction = true;
    debouncedInputFormattingEvent();
  }

  private incrementHandler(): void {
    this.value += this.increment;
    this.inputElement.stepUp();
    this.inputInteraction = true;
  }

  private decrementHandler(): void {
    this.value -= this.increment;
    this.inputElement.stepDown();
    this.inputInteraction = true;
  }

  private handleIncrementButtons(increment: number): void {
    this.value += increment;
    this.inputElement.stepUp(increment);
    this.inputInteraction = true;
  }

  private getValueRestrictedToMinMax(val: number): number {
    if (!this.inputConfig) {
      return val;
    }

    return val < this.inputConfig?.min
      ? this.inputConfig?.min
      : val > this.inputConfig?.max
      ? this.inputConfig?.max
      : val;
  }

  private dispatchMinMaxEvents(val: number): void {
    if (!this.inputInteraction || !this.inputConfig) {
      return;
    }

    if (val < this.inputConfig?.min) {
      this.dispatchEvents(IncrementInputEvent.MIN_VALUE_REACHED);
    }

    if (val > this.inputConfig?.max) {
      this.dispatchEvents(IncrementInputEvent.MAX_VALUE_REACHED);
    }
  }

  private dispatchEvents(eventType: string) {
    this.dispatchEvent(
      new CustomEvent(eventType, {
        detail: { value: this.value },
      })
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-increment-input': GzIncrementInputWebComponent;
  }
}
